import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatDetail } from "@/lib/api";
import { Calendar, Loader2, Video, Send, CheckCircle2 } from "lucide-react";

export default function OfficeHoursPage() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", country: "", topic: "",
    preferred: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) { toast.error("Name and email are required."); return; }
    setBusy(true);
    try {
      await api.post("/office-hours/book", form);
      setDone(true);
      toast.success("Booking received.");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <main data-testid="office-hours-page">
      <section className="mv-green-bg text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ Investor office hours</div>
          <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[0.98]">
            Ask us <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>anything.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-white/85 text-[15.5px] leading-relaxed">
            We hold a monthly investor Q&amp;A on Zoom. Bring your lawyer, your accountant, your sceptical uncle. Every question gets a plain-English answer.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 md:px-8 py-14 md:py-20">
        {done ? (
          <div className="mv-card p-8 flex items-start gap-4" style={{ background: "linear-gradient(180deg, #fff, rgba(37,211,102,0.06))" }} data-testid="office-hours-success">
            <CheckCircle2 size={26} style={{ color: "#0A7A2C" }}/>
            <div>
              <div className="font-display text-2xl mv-green-text">Booking received.</div>
              <p className="mt-2 text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
                Our team will confirm your slot within <strong>1 working day</strong>. You'll get a Zoom link by email.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mv-card p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="office-hours-form">
            <div className="md:col-span-2 flex items-center gap-3 pb-3 border-b mv-hairline">
              <Video size={20} className="mv-green-text"/>
              <div>
                <div className="font-display text-lg mv-green-text">Book a 20-minute Zoom slot</div>
                <div className="text-[12.5px]" style={{ color: "var(--mv-muted)" }}>Held once a month · you'll receive a confirmed time within 1 working day.</div>
              </div>
            </div>
            <Field label="Full name *"           v={form.full_name} on={set("full_name")} tid="oh-name"/>
            <Field label="Email *"               v={form.email}     on={set("email")}     type="email" tid="oh-email"/>
            <Field label="Phone / WhatsApp"      v={form.phone}     on={set("phone")}     tid="oh-phone"/>
            <Field label="Country you live in"   v={form.country}   on={set("country")}   tid="oh-country"/>
            <Field label="Preferred date / time" v={form.preferred} on={set("preferred")} placeholder="e.g. 5 Aug 2026 · 10am UK" tid="oh-preferred"/>
            <Field label="Topic in one line"     v={form.topic}     on={set("topic")}     placeholder="e.g. Escrow safeguards for South African diaspora" tid="oh-topic"/>
            <label className="block md:col-span-2">
              <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>What you'd like us to cover</span>
              <textarea value={form.message} onChange={set("message")} rows={4}
                        className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]" data-testid="oh-message"/>
            </label>
            <div className="md:col-span-2 pt-3 border-t mv-hairline flex justify-between items-center">
              <div className="inline-flex items-center gap-2 text-[12.5px]" style={{ color: "var(--mv-muted)" }}>
                <Calendar size={14}/> We hold slots between 08:00–20:00 UK time.
              </div>
              <button type="submit" disabled={busy} className="btn-green" data-testid="oh-submit">
                {busy ? <Loader2 className="animate-spin" size={16}/> : <Send size={16}/>}
                {busy ? "Sending…" : "Request my slot"}
              </button>
            </div>
          </form>
        )}
      </section>
    </main>
  );
}

function Field({ label, v, on, type = "text", placeholder, tid }) {
  return (
    <label className="block">
      <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>{label}</span>
      <input value={v} onChange={on} type={type} placeholder={placeholder}
             className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]" data-testid={tid}/>
    </label>
  );
}
